import React, { useState, useEffect } from "react";
import { TextField, Button, Typography, Checkbox } from "@mui/material";
import styles from "./ContactDetails.module.css";
import Logo from "../components/Logo";
import SideBackground from "../components/SideBackground";
import api from "../API/axios";
import NotificationSnackbar from "../components/NotificationSnackbar";
import useNotification from "../hooks/useNotification";

export default function LoginTwoFactor() {
  const { notification, showNotification, hideNotification } =
    useNotification();
  const [form, setForm] = useState({
    verificationCode: "",
    bypassCode: "",
  });
  const [errors, setErrors] = useState({
    verificationCode: "",
    bypassCode: "",
  });
  const [userInfo, setUserInfo] = useState(null);
  const [showBypass, setShowBypass] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [ischecked, setIsCheck] = useState(false);

  useEffect(() => {
    // Get user info from session/localStorage
    const userSession = localStorage.getItem("loginSession");
    if (!userSession) {
      window.location.href = "/";
      return;
    }

    const userData = JSON.parse(userSession);
    setUserInfo(userData);
    setLoading(false);

    // If email 2FA, send code immediately
    if (userData.twoFactorMethod === "email") {
      sendEmailCode();
    }

    // Start resend timer (user can resend after 1 minute)
    if (userData.twoFactorMethod === "email") {
      setResendTimer(60);
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, []);

  const sendEmailCode = async () => {
    try {
      const userSession = JSON.parse(localStorage.getItem("loginSession"));
      const tempToken = userSession?.tempToken;

      // Set temporary token for this request
      if (tempToken) {
        localStorage.setItem("token", tempToken);
      }

      await api.post("/TwoFactor/send-login-code", {
        userId: userInfo?.userId || userSession?.userId,
      });

      // Remove temporary token after request
      if (tempToken) {
        localStorage.removeItem("token");
      }
    } catch (err) {
      // Remove temporary token on error
      const userSession = JSON.parse(localStorage.getItem("loginSession"));
      const tempToken = userSession?.tempToken;
      if (tempToken) {
        localStorage.removeItem("token");
      }
      console.error("Failed to send email code:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    // Reset errors
    setErrors({ verificationCode: "", bypassCode: "" });

    const userSession = JSON.parse(localStorage.getItem("loginSession"));
    if (!userSession) {
      window.location.href = "/";
      return;
    }

    try {
      let endpoint = "";
      let payload = { userId: userSession.userId };

      // Set temporary token for this request
      const tempToken = userSession.tempToken;
      const originalToken = localStorage.getItem("token"); // Store original token

      if (tempToken) {
        localStorage.setItem("token", tempToken);

        // Make the request with tempToken
        if (showBypass) {
          // Verify bypass code
          if (!form.bypassCode || form.bypassCode.length !== 8) {
            showNotification(
              "Please enter a valid 8-character bypass code.",
              "error"
            );
            return;
          }
          endpoint = "/TwoFactor/verify-bypass";
          payload.bypassCode = form.bypassCode;
        } else if (userInfo.twoFactorMethod === "email") {
          // Verify email code
          if (!form.verificationCode || form.verificationCode.length !== 6) {
           
            showNotification(
              "Please enter a valid 6-digit verification code.",
              "error"
            );
            return;
          }
          endpoint = "/TwoFactor/verify-login";
          payload.userId = userInfo.userId;
          payload.verificationCode = form.verificationCode;
          payload.method = "email";
        } else if (userInfo.twoFactorMethod === "app") {
          // Verify app code
          if (!form.verificationCode || form.verificationCode.length !== 6) {
           showNotification(
              "Please enter a valid 6-digit verification code.",
              "error"
            );
            return;
          }
          endpoint = "/TwoFactor/verify-login";
          payload.userId = userInfo.userId;
          payload.verificationCode = form.verificationCode;
          payload.method = "app";
        }
      } else {
        // No tempToken available, this shouldn't happen in 2FA flow
        console.error("No tempToken found in session");
        showNotification("Session expired. Please login again.", "error");
        window.location.href = "/";
        return;
      }

      const response = await api.post(endpoint, payload);

      // Restore original token or remove tempToken
      if (originalToken) {
        localStorage.setItem("token", originalToken);
      } else {
        localStorage.removeItem("token");
      }

      // Debug: Log what we received from backend
      console.log("2FA Response from backend:", response.data);

      // Store full authentication token and user data from backend response
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("role", response.data.role);

      // Store complete user data for role-based access
      const userData = {
        id: response.data.userId || userInfo.userId,
        email: response.data.email || userInfo.email,
        name: response.data.name || userInfo.name,
        role: response.data.role,
        isActive: response.data.isActive,
      };

      // Debug: Log what we're storing
      console.log("Storing user data:", userData);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.removeItem("loginSession");

      // Redirect to role-specific dashboard using the role from backend
      const roleBasePath = {
        SuperAdmin: "/superadmin",
        ClientAdmin: "/clientadmin",
        Operator: "/operator",
        Consumer: "/consumer",
      };

      window.location.href =
        roleBasePath[response.data.role] || "/unauthorized";
    } catch (err) {
      // Restore original token on error
      const tempToken = userSession.tempToken;
      const originalToken = localStorage.getItem("token");

      if (tempToken) {
        if (originalToken && originalToken !== tempToken) {
          localStorage.setItem("token", originalToken);
        } else {
          localStorage.removeItem("token");
        }
      }

      console.error(err);
      if (showBypass) {
    showNotification(
        "Invalid bypass code. Please try again.",
        "error"
      );
      } else {
      showNotification(
        "The verification code is incorrect or has expired.",
        "error"
      );
      }
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setCanResend(false);
    setResendTimer(60);

    try {
      await sendEmailCode();
      showNotification("Verification code sent to your email.", "success");

      // Start timer again
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error(err);
      showNotification(
        "Failed to send verification code. Please try again.",
        "error"
      );
      setCanResend(true);
      setResendTimer(0);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!userInfo) {
    return <div>Session expired. Please login again.</div>;
  }

  return (
    <div className={styles.contactDetailsRoot}>
      <div className={styles.contactDetailsLeft}>
        <div className={styles.contactDetailsLogoWrap}>
          <Logo />
        </div>
        <div className={styles.contactDetailsForm}>
          <h2>Sign In</h2>
          <div
            style={{
              fontWeight: 500,
              fontSize: "1.1rem",
              color: "#888",
              textAlign: "center",
            }}
          >
            2 Factor Verification {showBypass && " Bypass"}
          </div>
          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              marginBottom: 12,
              color: "#666",
            }}
          >
            {userInfo.twoFactorMethod === "email" && !showBypass && (
              <p>
                Your one time code has been sent to your device, and is valid
                for 5 minutes. <br />
                Please enter the code to proceed.
              </p>
            )}
            {userInfo.twoFactorMethod === "app" && !showBypass && (
              <p>
                Please open your authenticator app and enter the 6-digit code.
              </p>
            )}
            {showBypass && <p>Please enter your bypass code below.</p>}
          </div>

          <form onSubmit={handleVerify}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              {!showBypass ? (
                <TextField
                  type="text"
                  label={"Verification Code"}
                  name="verificationCode"
                  value={form.verificationCode}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      verificationCode: e.target.value.replace(/[^0-9]/g, ""),
                    })
                  }
                  required
                  autoFocus
                  error={!!errors.verificationCode}
                  helperText={errors.verificationCode}
                  inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
                  sx={{
                    width: "100%",
                    maxWidth: 260,
                    marginBottom: 1,
                  }}
                />
              ) : (
                <TextField
                  type="text"
                  label="Bypass Code"
                  name="bypassCode"
                  value={form.bypassCode}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.bypassCode}
                  helperText={errors.bypassCode}
                  inputProps={{ maxLength: 20 }}
                  sx={{
                    width: "100%",
                    maxWidth: 260,
                    marginBottom: 3,
                  }}
                />
              )}
              {/* Resend button for email */}
              {userInfo.twoFactorMethod === "email" && !showBypass && (
                <div
                  style={{
                    fontSize: "12px",
                    width: "100%",
                    textAlign: "center",
                    marginBottom: "24px",
                    color: "#666",
                  }}
                >
                  <p
                    onClick={handleResend}
                    style={{ margin: 0, cursor: "pointer" }}
                  >
                    Not received the code?{" "}
                    <span style={{ textDecoration: "underline" }}>
                      {canResend ? "Resend" : `Resend in ${resendTimer}s`}
                    </span>
                  </p>
                </div>
              )}
              {showBypass && (
                <div
                  style={{
                    fontSize: "13px",
                    width: "100%",
                    textAlign: "center",
                    marginBottom: 12,
                    color: "#333",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontWeight: 700,
                  }}
                >
                  <Checkbox
                    size="small"
                    onClick={() => setIsCheck(!ischecked)}
                  />
                  <p>
                    I acknowledge that the entered code is a one time use,
                    <br />
                    and once used will no longer be valid
                  </p>
                </div>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  mt: 1,
                  maxWidth: 260,
                  width: "100%",
                  backgroundColor: "#AB2C29",
                  "&:hover": { backgroundColor: "#b71c1c" },
                }}
                disabled={showBypass && !ischecked}
              >
                Sign In
              </Button>
            </div>
          </form>

          {/* Bypass option */}
          <div style={{ textAlign: "center", marginTop: "24px" }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Can't access your{" "}
              {userInfo.twoFactorMethod === "email"
                ? "email"
                : "authenticator app"}
              ?
            </Typography>
            <Typography
              variant="body2"
              color="var(--color-primary)"
              sx={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => setShowBypass(!showBypass)}
            >
              {showBypass ? "Use Verification Code" : "Use Bypass Code"}
            </Typography>
          </div>
        </div>
      </div>
      <SideBackground>
        <div className={styles.contactDetailsBgOverlay}></div>
      </SideBackground>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </div>
  );
}
